import CallMadeIcon from '@mui/icons-material/CallMade';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import ThumbsUpDownIcon from '@mui/icons-material/ThumbsUpDown';
import { Box, Collapse, Stack, TablePagination, Tooltip } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import PaiLink from 'features/ui/base-components/pai-link';
import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { formatDateTimeInTimeZone } from 'utils/date.utils';
import { isConnectionRequestStep, stepLabelKey } from 'utils/campaign-step.utils';
import { LinkedinContact, MessageDirections } from './campaign-statistics-types';

interface Props {
  contacts: LinkedinContact[];
  timeZone: string | undefined;
  contactDrawerLoading: boolean;

  handleOpenContactDrawer(linkedInURL: string, profileId?: string, funnelId?: string): () => void;
}

const Message: React.FC<{ text: string; functionName?: string }> = ({ text, functionName }) => {
  const { t } = useTranslation();
  const { classes, cx } = useStyles();

  const [showMore, setShowMore] = useState(false);

  // An empty body is only a "connection request without a note" for the
  // send-connection-request step. Withdraw, visit, wait and the check steps
  // also carry no text — for those an empty body is normal, so render nothing
  // rather than the misleading no-note label.
  if (!text || !text.trim()) {
    return isConnectionRequestStep(functionName) ? (
      <span className={classes.emptyMessage}>{t('connectionRequestNoNote')}</span>
    ) : null;
  }

  return (
    <div
      className={cx({
        [classes.messageText]: showMore,
      })}
      style={{ display: 'inline' }}
    >
      <Collapse in={!showMore} timeout={0}>
        {_.truncate(text, { length: 200 })}
        {text.length > 200 ? (
          <PaiLink
            text={showMore ? t('less') : t('more')}
            variant={'primary'}
            underline={'always'}
            size={'md'}
            inline={!showMore}
            ml={!showMore ? '4px' : undefined}
            action={() => setShowMore((prev) => !prev)}
          />
        ) : null}
      </Collapse>
      <Collapse in={showMore} timeout={0} sx={{ lineBreak: 'anywhere' }}>
        {text}
        {text.length > 200 ? (
          <PaiLink
            text={showMore ? t('less') : t('more')}
            variant={'primary'}
            underline={'always'}
            size={'md'}
            inline={!showMore}
            ml={!showMore ? '4px' : undefined}
            action={() => setShowMore((prev) => !prev)}
          />
        ) : null}
      </Collapse>
    </div>
  );
};

const CampaignMessageChronicle: React.FC<Props> = ({ contacts = [], timeZone, contactDrawerLoading, handleOpenContactDrawer }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const myTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedContacts = contacts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <div>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell className={classes.tableHead} sx={{ width: '15%' }}>
              {t('contact')}
            </TableCell>
            <TableCell className={classes.tableHead} sx={{ width: '7%' }}>
              {t('reply')}
            </TableCell>
            <TableCell className={classes.tableHead} sx={{ width: '78%' }}>
              {t('messages')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {!_.isEmpty(paginatedContacts) ? (
            paginatedContacts.map((contact, index) => (
              <TableRow key={index}>
                <TableCell className={classes.tableBody}>
                  {!_.isNil(contact.linkedInURL) && !_.isNil(contact.profileIds?.linkedin) && !_.isNil(contact.funnelId) ? (
                    <Stack direction={'row'} alignItems={'center'} justifyContent={'flex-start'} gap={1} sx={{ height: 'calc(36.5px - 12px)' }}>
                      <PaiLink
                        text={contact.name?.trim() !== '' ? contact.name : `...${_.slice(contact.linkedInURL, -20).join('')}`}
                        variant={'primary'}
                        underline={'always'}
                        size={'md'}
                        wordBreak={'break-all'}
                        disabled={contactDrawerLoading}
                        action={handleOpenContactDrawer(contact.linkedInURL, contact.profileIds.linkedin, contact.funnelId)}
                      />
                      <OpenInNewIcon className={classes.icon} />
                    </Stack>
                  ) : contact.name?.trim() !== '' ? (
                    contact.name
                  ) : (
                    `...${_.slice(contact.linkedInURL, -20).join('')}`
                  )}
                </TableCell>
                <TableCell className={classes.tableBody}>
                  {_.isNil(contact.sentiment) ? (
                    '-'
                  ) : (
                    <Tooltip title={`${t('reason')}: ${contact.reason || '-'}`} disableHoverListener={_.isNil(contact.reason)}>
                      {contact.sentiment.includes('positive') ? (
                        <ThumbUpAltIcon className={cx(classes.icon, 'positive')} />
                      ) : contact.sentiment.includes('negative') ? (
                        <ThumbDownAltIcon className={cx(classes.icon, 'negative')} />
                      ) : (
                        <ThumbsUpDownIcon className={cx(classes.icon, 'balanced')} />
                      )}
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell sx={{ padding: 0 }}>
                  <Box>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell className={classes.tableHead} sx={{ width: '15%' }}>
                            {t('date')}
                          </TableCell>
                          <TableCell className={classes.tableHead} sx={{ width: '15%' }}>
                            {t('campaignStep')}
                          </TableCell>
                          <TableCell className={classes.tableHead} sx={{ width: '70%' }}>
                            {t('message')}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(contact.messages ?? []).map((message, index, messages) => (
                          <TableRow key={index}>
                            <TableCell
                              className={classes.tableBody}
                              component="th"
                              scope="row"
                              sx={{ border: index === messages.length - 1 ? 'none' : undefined }}
                            >
                              {formatDateTimeInTimeZone(new Date(message.date).toISOString(), 'Pp', timeZone || myTimeZone)}
                            </TableCell>
                            <TableCell className={classes.tableBody} sx={{ border: index === messages.length - 1 ? 'none' : undefined }}>
                              {!_.isNil(message.step) && !_.isNil(message.step.functionName)
                                ? `${t(stepLabelKey(message.step.functionName))} (${message.step.stepIndex + 1})`
                                : '-'}
                            </TableCell>
                            <TableCell className={classes.tableBody} sx={{ border: index === messages.length - 1 ? 'none' : undefined }}>
                              <Stack direction={'row'} gap={1} alignItems={'flex-start'}>
                                {message.direction === MessageDirections.SENT ? (
                                  <CallMadeIcon className={classes.icon} sx={{ marginTop: '2px' }} />
                                ) : (
                                  <CallReceivedIcon className={classes.icon} sx={{ marginTop: '2px' }} />
                                )}
                                <Message text={message.text} functionName={message.step?.functionName} />
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell className={classes.tableBody} colSpan={4} sx={{ textAlign: 'center' }}>
                {t('noMessagesSent')}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {contacts.length > 0 && (
        <TablePagination
          component="div"
          count={contacts.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
          labelRowsPerPage={t('contactsPerPage')}
          labelDisplayedRows={({ from, to, count }) => `${from} - ${to} ${t('of')} ${count}`}
        />
      )}
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  tableHead: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 'bold',
    paddingLeft: 0,
  },
  tableBody: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
    paddingLeft: 0,
    verticalAlign: 'top',
  },
  messageText: {
    whiteSpace: 'pre-wrap',
  },
  emptyMessage: {
    fontStyle: 'italic',
    opacity: 0.6,
  },
  icon: {
    fontSize: theme.typography.pxToRem(16),
    '&.positive': {
      color: theme.palette.success.main,
      fontSize: theme.typography.pxToRem(20),
      marginTop: '2px',
    },
    '&.negative': {
      color: theme.palette.error.main,
      fontSize: theme.typography.pxToRem(20),
      marginTop: '2px',
    },
    '&.balanced': {
      color: theme.palette.warning.main,
      fontSize: theme.typography.pxToRem(20),
      marginTop: '2px',
    },
  },
}));

export default CampaignMessageChronicle;
